// components/Reports/PaintingCoatReportContent.js
import React, { useMemo } from "react";
import { useRfi } from "../../contexts/RfiContext";
import AmbientSurfaceConditionTable from "./AmbientSurfaceConditionTable";

/**
 * PaintingCoatReportContent - Reusable report component for all paint coat stages
 * Displays coating identification, ambient conditions, and images for any coat type
 *
 * @param {Object} props
 * @param {string} props.coatType - Type of coat: "primer", "secondcoat", or "topcoat"
 * @param {string} props.stageTitle - Optional custom title (e.g., "PRIMER COAT", "SECOND COAT", "TOP COAT")
 */
const PaintingCoatReportContent = ({ coatType, stageTitle }) => {
  // Get all Context data at the top level (hooks must be called unconditionally)
  const { stagesData } = useRfi();
  const primerData = stagesData.primer.data;
  const secondcoatData = stagesData.secondcoat.data;
  const topcoatData = stagesData.topcoat.data;

  // Select the appropriate data based on coat type
  const reduxData = useMemo(() => {
    switch (coatType) {
      case "primer":
        return primerData;
      case "secondcoat":
      case "second":
        return secondcoatData;
      case "top":
      case "topcoat":
        return topcoatData;
      default:
        return null;
    }
  }, [coatType, primerData, secondcoatData, topcoatData]);

  // Generate default title if not provided
  const defaultTitle = useMemo(() => {
    switch (coatType) {
      case "primer":
        return "PRIMER COAT";
      case "secondcoat":
      case "second":
        return "SECOND COAT";
      case "top":
      case "topcoat":
        return "TOP COAT";
      default:
        return "COATING";
    }
  }, [coatType]);

  const title = stageTitle || defaultTitle;

  // Map Redux data structure to component format
  const coatData = useMemo(() => {
    if (!reduxData?.coatings?.[0]) return null;

    const coating = reduxData.coatings[0];
    const coatingImages = reduxData.coating_images || [];
    const paintingChecklistImages = reduxData.painting_checklist_images || [];
    const paintingChecklistParts = reduxData.painting_checklist_parts || [];
    const baseUrl = process.env.REACT_APP_API_BASE_URL;

    // Determine weatherCondition from booleans
    let weatherCondition = "";
    if (coating.clear) weatherCondition = "Clear";
    else if (coating.rain) weatherCondition = "Rain";
    else if (coating.cloudy) weatherCondition = "Cloudy";

    // Map coating images to expected format with proper URL handling
    const pictures = coatingImages.map((img) => {
      let imageUrl = "";
      if (img.image_url) {
        imageUrl = img.image_url.startsWith("http")
          ? img.image_url
          : baseUrl + img.image_url;
      }
      return { url: imageUrl, id: img.image_id };
    });

    // Map checklist images
    const checklistPictures = paintingChecklistImages.map((img) => {
      let imageUrl = "";
      if (img.image_url) {
        imageUrl = img.image_url.startsWith("http")
          ? img.image_url
          : baseUrl + img.image_url;
      }
      return { url: imageUrl, id: img.image_id };
    });

    return {
      weatherCondition,
      coatingIdentification: {
        timeApplication: coating.time_application
          ? new Date(coating.time_application).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : "",
        painterId: coating.painter_id || "",
        methodApplication: coating.method_application || "",
        brandNameColour: coating.brand_name_colour_ral || "",
        mixingRatio: coating.mixing_ratio || "",
        partABatchNo: coating.part_a_batch_no || "",
        partADOM: coating.part_a_dom || "",
        partBBatchNo: coating.part_b_batch_no || "",
        partBDOM: coating.part_b_dom || "",
        thinnerBatchNo: coating.thinner_batch_no || "",
        thinnerDOM: coating.thinner_dom || "",
        surfaceCondition: coating.surface_condition || "",
        stripeCoat: coating.stripe_coat || "",
        wetFilmThickness: coating.wet_film_thickness ?? "",
        dryFilmThickness: coating.dry_film_thickness_total ?? "",
        adhesionTest: coating.adhesion_test || "",
        fullCure: coating.full_cure ? "Yes" : "No",
        dry: coating.dry ? "Yes" : "No",
        drawingForSpecificRequirement:
          coating.drawing_for_specific_requirement || "",
        dftSpotTakenEvery: coating.dft_spot_taken_every || "",
        remarks: coating.remarks || "",
      },
      pictures,
      checklistPictures,
      parts: paintingChecklistParts,
      ambientCondition: {
        time: coating.time ? new Date(coating.time).toLocaleDateString() : "",
        dryBulb: coating.dry_bulb ?? "",
        wetBulb: coating.wet_bulb ?? "",
        humidity: coating.humidity ?? "",
        dewPoint: coating.dew_point ?? "",
        substrateTemp: coating.substrate_temp ?? "",
        substrateDewPoint: coating.substrate_temp_minus_dew_point ?? "",
        status: coating.status || "",
        finalInspection: coating.final_inspection || "",
      },
    };
  }, [reduxData]);

  if (!coatData) {
    return <div>No {title.toLowerCase()} data available</div>;
  }

  const { coatingIdentification: ci } = coatData;

  return (
    <div style={{ width: "100%", fontSize: "10px", color: "#222222" }}>
      {/* Weather Condition Section */}
      <div
        style={{ marginBottom: "8px" }}
        data-pdf-section="painting-coat-report"
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #cbd5e0",
            marginBottom: "8px",
          }}
        >
          <tbody>
            {/* Weather Condition */}
            <tr>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  fontWeight: "bold",
                  width: "30%",
                  backgroundColor: "#f8f9fa",
                }}
              >
                Weather Condition
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "8px 10px",
                  backgroundColor: "#fff",
                }}
              >
                {coatData.weatherCondition || "—"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Ambient & Surface Condition Section */}
      <AmbientSurfaceConditionTable data={coatData.ambientCondition} />

      {/* Coating Identification Section */}
      <div style={{ marginBottom: "8px" }}>
        <div
          style={{
            fontSize: "12px",
            fontWeight: "bold",
            marginBottom: "10px",
            padding: "6px 10px",
            backgroundColor: "#073642",
            color: "#fff",
            letterSpacing: "0.5px",
          }}
          data-pdf-section="coating-identification-title"
        >
          COATING IDENTIFICATION
        </div>

        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #cbd5e0",
            marginBottom: "8px",
          }}
        >
          <tbody>
            {/* Row 1: Time Application & Painter ID */}
            <tr data-pdf-section="coating-identification-row1-nomargin">
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  width: "18%",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Time Application
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  width: "32%",
                  backgroundColor: "#fff",
                }}
              >
                {ci.timeApplication || "—"}
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  width: "18%",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Painter ID
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  width: "32%",
                  backgroundColor: "#fff",
                }}
              >
                {ci.painterId || "—"}
              </td>
            </tr>

            {/* Row 2: Method Application & Brand Name */}
            <tr data-pdf-section="coating-identification-row2-nomargin">
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Method Application
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.methodApplication || "—"}
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Brand Name, Colour & RAL
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.brandNameColour || "—"}
              </td>
            </tr>

            {/* Row 3: Mixing Ratio & Part A */}
            <tr data-pdf-section="coating-identification-row3-nomargin">
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Mixing Ratio
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.mixingRatio || "—"}
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Part A Batch No & DOM
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.partABatchNo || "—"} / {ci.partADOM || "—"}
              </td>
            </tr>

            {/* Row 4: Part B & Thinner */}
            <tr data-pdf-section="coating-identification-row4-nomargin">
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Part B Batch No & DOM
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.partBBatchNo || "—"} / {ci.partBDOM || "—"}
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Thinner Batch No & DOM
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.thinnerBatchNo || "—"} / {ci.thinnerDOM || "—"}
              </td>
            </tr>

            {/* Row 5: Surface Condition & Stripe Coat */}
            <tr data-pdf-section="coating-identification-row5-nomargin">
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Surface Condition
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.surfaceCondition || "—"}
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Stripe Coat
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.stripeCoat || "—"}
              </td>
            </tr>

            {/* Row 6: Wet Film Thickness & Dry Film Thickness */}
            <tr data-pdf-section="coating-identification-row6-nomargin">
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Wet Film Thickness
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.wetFilmThickness ? `${ci.wetFilmThickness} µm` : "—"}
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Dry Film Thickness
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.dryFilmThickness ? `${ci.dryFilmThickness} µm` : "—"}
              </td>
            </tr>

            {/* Row 7: Adhesion Test & Full Cure */}
            <tr data-pdf-section="coating-identification-row7-nomargin">
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Adhesion Test
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.adhesionTest || "—"}
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Full Cure
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.fullCure}
              </td>
            </tr>

            {/* Row 8: Dry & Drawing for Specific Requirement */}
            <tr data-pdf-section="coating-identification-row8-nomargin">
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Dry
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.dry}
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                Drawing for Specific Requirement
              </td>
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.drawingForSpecificRequirement || "—"}
              </td>
            </tr>

            {/* Row 9: DFT Spot Taken Every (full width since next field is remarks) */}
            <tr data-pdf-section="coating-identification-row9-nomargin">
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  fontSize: "10px",
                }}
              >
                DFT Spot Taken Every
              </td>
              <td
                colSpan={3}
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                }}
              >
                {ci.dftSpotTakenEvery || "—"}
              </td>
            </tr>

            {/* Row 10: Remarks (Full Width) */}
            <tr data-pdf-section="coating-identification-row10">
              <td
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  fontWeight: "bold",
                  backgroundColor: "#f8f9fa",
                  verticalAlign: "top",
                  fontSize: "10px",
                }}
              >
                Remarks
              </td>
              <td
                colSpan={3}
                style={{
                  border: "1px solid #cbd5e0",
                  padding: "6px 8px",
                  backgroundColor: "#fff",
                  whiteSpace: "pre-wrap",
                }}
              >
                {ci.remarks || "—"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Coating Images Section */}
      {coatData.pictures && coatData.pictures.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {coatData.pictures.reduce((acc, picture, index) => {
            if (index % 5 === 0) {
              const isFirstRow = index === 0;
              acc.push(
                <div
                  key={`image-row-${index}`}
                  data-pdf-section={`images-section-${index}`}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%",
                    paddingBottom: "10px",
                  }}
                >
                  {isFirstRow && (
                    <div
                      style={{
                        fontWeight: "bold",
                        marginBottom: "10px",
                        fontSize: "11px",
                        color: "#073642",
                        paddingTop: "10px",
                      }}
                    >
                      Coating Drawings/Pictures:
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      width: "100%",
                    }}
                  >
                    {coatData.pictures
                      .slice(index, index + 5)
                      .map((picture, innerIndex) => (
                        <div
                          key={picture.id || index + innerIndex}
                          style={{
                            flex: "1",
                            minWidth: "0",
                            maxWidth: "calc(25% - 9px)",
                            height: "100px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "4px",
                            overflow: "hidden",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          }}
                        >
                          <img
                            src={picture.url}
                            alt={`${title} Coating ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              console.error(
                                `Failed to load image: ${picture.url}`
                              );
                              e.target.style.display = "none";
                            }}
                            onLoad={() => {
                              console.log(
                                `✅ Image loaded successfully: ${picture.url}`
                              );
                            }}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              );
            }
            return acc;
          }, [])}
        </div>
      )}

      {/* Painting Checklist Parts Table */}
      {coatData.parts && coatData.parts.length > 0 && (
        <div
          style={{
            marginTop: "8px",
            pageBreakInside: "avoid",
            breakInside: "avoid",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: "bold",
              marginBottom: "10px",
              padding: "6px 10px",
              backgroundColor: "#073642",
              color: "#fff",
              letterSpacing: "0.5px",
            }}
            data-pdf-section="painting-checklist-title"
          >
            PAINTING CHECKLIST
          </div>

          <div data-pdf-section="painting-checklist-table">
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                border: "1px solid #cbd5e0",
                marginBottom: "8px",
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      border: "1px solid #cbd5e0",
                      padding: "8px 10px",
                      backgroundColor: "#f8f9fa",
                      fontWeight: "bold",
                      textAlign: "left",
                      fontSize: "10px",
                      width: "8%",
                    }}
                  >
                    No
                  </th>
                  <th
                    style={{
                      border: "1px solid #cbd5e0",
                      padding: "8px 10px",
                      backgroundColor: "#f8f9fa",
                      fontWeight: "bold",
                      textAlign: "left",
                      fontSize: "10px",
                      width: "20%",
                    }}
                  >
                    Part/Area
                  </th>
                  <th
                    style={{
                      border: "1px solid #cbd5e0",
                      padding: "8px 10px",
                      backgroundColor: "#f8f9fa",
                      fontWeight: "bold",
                      textAlign: "left",
                      fontSize: "10px",
                      width: "20%",
                    }}
                  >
                    Part/Location
                  </th>
                  <th
                    style={{
                      border: "1px solid #cbd5e0",
                      padding: "8px 10px",
                      backgroundColor: "#f8f9fa",
                      fontWeight: "bold",
                      textAlign: "left",
                      fontSize: "10px",
                    }}
                  >
                    Item/Part Inspection
                  </th>
                  <th
                    style={{
                      border: "1px solid #cbd5e0",
                      padding: "8px 10px",
                      backgroundColor: "#f8f9fa",
                      fontWeight: "bold",
                      textAlign: "center",
                      fontSize: "10px",
                      width: "14%",
                    }}
                  >
                    Visual Check
                  </th>
                  <th
                    style={{
                      border: "1px solid #cbd5e0",
                      padding: "8px 10px",
                      backgroundColor: "#f8f9fa",
                      fontWeight: "bold",
                      textAlign: "left",
                      fontSize: "10px",
                      width: "14%",
                    }}
                  >
                    Remark/Note
                  </th>
                </tr>
              </thead>
              <tbody>
                {coatData.parts.map((part, partIndex) => {
                  const items = part.painting_part_items || [];
                  const rowSpan = items.length || 1;

                  return items.length > 0 ? (
                    items.map((item, itemIndex) => (
                      <tr key={`${partIndex}-${itemIndex}`}>
                        {itemIndex === 0 && (
                          <>
                            <td
                              rowSpan={rowSpan}
                              style={{
                                border: "1px solid #cbd5e0",
                                padding: "8px 10px",
                                backgroundColor: "#fff",
                                textAlign: "center",
                              }}
                            >
                              {partIndex + 1}
                            </td>
                            <td
                              rowSpan={rowSpan}
                              style={{
                                border: "1px solid #cbd5e0",
                                padding: "8px 10px",
                                backgroundColor: "#fff",
                              }}
                            >
                              {part.part_name || "—"}
                            </td>
                            <td
                              rowSpan={rowSpan}
                              style={{
                                border: "1px solid #cbd5e0",
                                padding: "8px 10px",
                                backgroundColor: "#fff",
                              }}
                            >
                              {part.part_location || "—"}
                            </td>
                          </>
                        )}
                        <td
                          style={{
                            border: "1px solid #cbd5e0",
                            padding: "8px 10px",
                            backgroundColor: "#fff",
                          }}
                        >
                          {item.item_name || item.inspection_point_name || "—"}
                        </td>
                        <td
                          style={{
                            border: "1px solid #cbd5e0",
                            padding: "8px 10px",
                            backgroundColor: "#fff",
                            textAlign: "center",
                          }}
                        >
                          {item.visual_check ? "Yes" : "No"}
                        </td>
                        <td
                          style={{
                            border: "1px solid #cbd5e0",
                            padding: "8px 10px",
                            backgroundColor: "#fff",
                          }}
                        >
                          {item.remark || "—"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr key={partIndex}>
                      <td
                        style={{
                          border: "1px solid #cbd5e0",
                          padding: "8px 10px",
                          backgroundColor: "#fff",
                          textAlign: "center",
                        }}
                      >
                        {partIndex + 1}
                      </td>
                      <td
                        style={{
                          border: "1px solid #cbd5e0",
                          padding: "8px 10px",
                          backgroundColor: "#fff",
                        }}
                      >
                        {part.part_name || "—"}
                      </td>
                      <td
                        style={{
                          border: "1px solid #cbd5e0",
                          padding: "8px 10px",
                          backgroundColor: "#fff",
                        }}
                      >
                        {part.part_location || "—"}
                      </td>
                      <td
                        colSpan={3}
                        style={{
                          border: "1px solid #cbd5e0",
                          padding: "8px 10px",
                          backgroundColor: "#fff",
                          textAlign: "center",
                          fontStyle: "italic",
                          color: "#666",
                        }}
                      >
                        No inspection points
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Painting Checklist Images Section */}
      {coatData.checklistPictures && coatData.checklistPictures.length > 0 && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          {coatData.checklistPictures.reduce((acc, picture, index) => {
            if (index % 5 === 0) {
              const isFirstRow = index === 0;
              acc.push(
                <div
                  key={`image-row-${index}`}
                  data-pdf-section={`images-section-${index}`}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    width: "100%",
                  }}
                >
                  {isFirstRow && (
                    <div
                      style={{
                        fontWeight: "bold",
                        marginBottom: "10px",
                        fontSize: "11px",
                        color: "#073642",
                        paddingTop: "3px",
                      }}
                    >
                      Images for Painting Checklist:
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      width: "100%",
                    }}
                  >
                    {coatData.pictures
                      .slice(index, index + 5)
                      .map((picture, innerIndex) => (
                        <div
                          key={picture.id || index + innerIndex}
                          style={{
                            flex: "1",
                            minWidth: "0",
                            maxWidth: "calc(25% - 9px)",
                            height: "100px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "4px",
                            overflow: "hidden",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                          }}
                        >
                          <img
                            src={picture.url}
                            alt={`${title} Coating ${index + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(e) => {
                              console.error(
                                `Failed to load image: ${picture.url}`
                              );
                              e.target.style.display = "none";
                            }}
                            onLoad={() => {
                              console.log(
                                `✅ Image loaded successfully: ${picture.url}`
                              );
                            }}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              );
            }
            return acc;
          }, [])}
        </div>
      )}
    </div>
  );
};

export default PaintingCoatReportContent;
